import { useState, useRef, useCallback, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { Upload, X, Image as ImageIcon, Pencil, PenTool, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  useSafetyDiagrams,
  useUploadDiagram,
  useCreateCanvasDiagram,
  useSaveDiagramCanvas,
} from '@/hooks/use-safety';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

import { RiggingCanvas } from '@/components/safety/rigging-canvas';
import type { DiagramFile } from '@/components/safety/rigging-canvas';
import { toBackendSchema, fromBackendSchema } from '@/components/safety/rigging-canvas/schema-adapter';
import {
  canRecoverDiagramDraft,
  cloneDiagramWithFreshElementIds,
  createDiagramSaveSnapshot,
  clearDiagramStorage,
  diagramSignature,
  DIAGRAM_SCHEMA_VERSION,
  loadDiagramFileFromStorage,
  reconcileDiagramStorage,
  resolveDiagramSaveTarget,
  saveDiagramToStorage,
} from '@/components/safety/rigging-canvas/persistence';
import type { DiagramSaveSnapshot } from '@/components/safety/rigging-canvas/persistence';

interface SafetyDiagramsSectionProps {
  contentType: string;
  objectId: number;
  /** Diagrams can only be changed while the parent plan is a draft. */
  canEdit?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

type SaveStatus = 'clean' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict';

type DiagramConflictResponse = {
  detail?: string;
  code?: string;
  latest_revision_number?: number;
};

type DiagramSaveConflict = {
  diagramId: number;
  snapshot: DiagramSaveSnapshot;
  expectedRevision: number | null;
  baseSignature: string;
  latestRevision: number | null;
  message: string;
};

type DiagramRenameConflict = {
  diagramId: number;
  message: string;
};

function dataURLtoBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)?.[1] ?? 'image/png';
  const bstr = atob(parts[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
}

function createCanvasFormData(diagram: DiagramFile, pngDataUrl: string | null): FormData {
  const backendData = toBackendSchema(diagram);
  const formData = new FormData();
  const pngBlob = pngDataUrl ? dataURLtoBlob(pngDataUrl) : null;
  if (pngBlob) {
    const pngFile = new File([pngBlob], 'rigging-diagram.png', { type: 'image/png' });
    formData.append('file', pngFile);
    formData.append('file_name', 'rigging-diagram.png');
  }
  formData.append('canvas_data', JSON.stringify(backendData));
  formData.append('schema_version', String(DIAGRAM_SCHEMA_VERSION));
  return formData;
}

function isDiagramRevisionConflict(error: unknown): error is { response: { data?: DiagramConflictResponse } } {
  return (
    isAxiosError<DiagramConflictResponse>(error)
    && error.response?.status === 409
    && (
      error.response.data?.code === 'diagram_revision_conflict'
      || error.response.data?.code === 'diagram_revision_required'
    )
  );
}

export function SafetyDiagramsSection({ contentType, objectId, onDirtyChange, canEdit = true }: SafetyDiagramsSectionProps) {
  const timeZone = useOrgTimeZone();
  const { data: diagramsData, isLoading, refetch: refetchDiagrams } = useSafetyDiagrams(contentType, objectId);
  const diagrams = diagramsData?.results ?? [];
  const uploadMutation = useUploadDiagram();
  const createCanvasMutation = useCreateCanvasDiagram();
  const saveCanvasMutation = useSaveDiagramCanvas();
  const renameCanvasMutation = useSaveDiagramCanvas();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exportPngRef = useRef<(() => string | null) | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [showRopeCanvas, setShowRopeCanvas] = useState(false);
  const [editingDiagramId, setEditingDiagramId] = useState<number | null>(null);
  const [canvasSaveStatus, setCanvasSaveStatus] = useState<SaveStatus>('clean');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameConflict, setRenameConflict] = useState<DiagramRenameConflict | null>(null);
  const [currentDiagram, setCurrentDiagram] = useState<DiagramFile | null>(null);
  const [persistedSignature, setPersistedSignature] = useState(diagramSignature([], 1));
  const [persistedRevision, setPersistedRevision] = useState<number | null>(null);
  const persistedRevisionRef = useRef<number | null>(null);
  const [saveConflict, setSaveConflict] = useState<DiagramSaveConflict | null>(null);
  const saveConflictRef = useRef<DiagramSaveConflict | null>(null);
  const latestDiagramRef = useRef<DiagramFile | null>(null);
  const hasAutoOpened = useRef(false);
  // Mirrors canEdit inside stable callbacks: a locked plan never persists canvas changes.
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const editingIdRef = useRef<number | null>(null);
  editingIdRef.current = editingDiagramId;
  const showRopeCanvasRef = useRef(showRopeCanvas);
  showRopeCanvasRef.current = showRopeCanvas;
  // Stable key for RiggingCanvas — only changes on explicit diagram switch, not on create ID assignment
  const canvasKeyRef = useRef<string | number>('new');
  const newCanvasSessionRef = useRef(0);
  const documentScopeRef = useRef(`${contentType}:${objectId}`);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCreatingRef = useRef(false);
  const [createInFlight, setCreateInFlight] = useState(false);
  const createRequestSequenceRef = useRef(0);
  const activeCreateRequestRef = useRef<number | null>(null);
  const updatingDiagramIdsRef = useRef(new Set<number>());
  const pendingUpdateDiagramsRef = useRef(new Map<number, DiagramSaveSnapshot & { expectedRevision: number | null }>());
  const pendingCreateDiagramRef = useRef<DiagramFile | null>(null);
  const confirmedRevisionByDiagramRef = useRef(new Map<number, number>());

  useEffect(() => {
    persistedRevisionRef.current = persistedRevision;
  }, [persistedRevision]);

  useEffect(() => {
    saveConflictRef.current = saveConflict;
  }, [saveConflict]);

  useEffect(() => {
    const nextScope = `${contentType}:${objectId}`;
    if (documentScopeRef.current === nextScope) return;
    documentScopeRef.current = nextScope;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    loadedIdRef.current = null;
    editingIdRef.current = null;
    latestDiagramRef.current = null;
    pendingCreateDiagramRef.current = null;
    pendingUpdateDiagramsRef.current.clear();
    updatingDiagramIdsRef.current.clear();
    confirmedRevisionByDiagramRef.current.clear();
    isCreatingRef.current = false;
    activeCreateRequestRef.current = null;
    setCreateInFlight(false);
    setEditingDiagramId(null);
    setCurrentDiagram(null);
    setShowRopeCanvas(false);
    setCanvasSaveStatus('clean');
    setPersistedRevision(null);
    persistedRevisionRef.current = null;
    setPersistedSignature(diagramSignature([], 1));
    setSaveConflict(null);
    setRenameConflict(null);
    setRenamingId(null);
    hasAutoOpened.current = false;
    canvasKeyRef.current = 'new';
  }, [contentType, objectId]);

  // Canvas-based diagrams
  const canvasDiagrams = diagrams.filter((d) => d.canvas_data !== null);
  // Image-based diagrams (legacy uploads)
  const imageDiagrams = diagrams.filter((d) => d.file && !d.canvas_data);

  const soleCanvasDiagramId = canvasDiagrams.length === 1
    ? canvasDiagrams[0].id
    : null;

  // Auto-open a single canvas diagram once per parent document
  useEffect(() => {
    if (
      !canEdit
      || hasAutoOpened.current
      || soleCanvasDiagramId === null
      || showRopeCanvas
      || editingDiagramId
    ) return;
    hasAutoOpened.current = true;
    canvasKeyRef.current = soleCanvasDiagramId;
    setEditingDiagramId(soleCanvasDiagramId);
    setShowRopeCanvas(true);
  }, [
    canEdit,
    contentType,
    objectId,
    soleCanvasDiagramId,
    showRopeCanvas,
    editingDiagramId,
  ]);

  // Load diagram from backend when editingDiagramId changes
  const loadedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!editingDiagramId) {
      loadedIdRef.current = null;
      setCurrentDiagram(null);
      setPersistedSignature(diagramSignature([], 1));
      setPersistedRevision(null);
      persistedRevisionRef.current = null;
      latestDiagramRef.current = null;
      return;
    }
    // Already loaded this diagram — skip
    if (loadedIdRef.current === editingDiagramId) return;
    const diagram = diagrams.find((d) => d.id === editingDiagramId);
    if (diagram?.canvas_data) {
      loadedIdRef.current = editingDiagramId;
      const loaded = fromBackendSchema(diagram.canvas_data);
      const backendRevision = diagram.latest_revision_number ?? null;
      const backendSignature = loaded
        ? diagramSignature(loaded.elements, loaded.nextLegendNumber ?? 1)
        : diagramSignature([], 1);
      const stored = loadDiagramFileFromStorage(
        `${contentType}:${objectId}:${editingDiagramId}`,
      );
      const storedSignature = stored
        ? diagramSignature(stored.elements, stored.nextLegendNumber)
        : null;
      const hasStaleDraft = Boolean(
        loaded
        && stored
        && !canRecoverDiagramDraft(
          stored,
          loaded.elements,
          loaded.nextLegendNumber ?? 1,
          backendRevision,
        )
        && storedSignature !== backendSignature,
      );
      if (loaded && stored && hasStaleDraft) {
        const conflictedDiagram: DiagramFile = {
          schemaVersion: DIAGRAM_SCHEMA_VERSION,
          savedAt: stored.savedAt ?? new Date().toISOString(),
          elements: stored.elements,
          calibration: stored.calibration,
          nextLegendNumber: stored.nextLegendNumber,
          baseSignature: stored.baseSignature ?? undefined,
          baseRevision: stored.baseRevision,
        };
        const baseSignature = stored.baseSignature ?? backendSignature;
        setCurrentDiagram(conflictedDiagram);
        setPersistedSignature(baseSignature);
        setPersistedRevision(stored.baseRevision);
        persistedRevisionRef.current = stored.baseRevision;
        latestDiagramRef.current = conflictedDiagram;
        setSaveConflict({
          diagramId: editingDiagramId,
          snapshot: createDiagramSaveSnapshot(conflictedDiagram),
          expectedRevision: stored.baseRevision,
          baseSignature,
          latestRevision: backendRevision,
          message: (
            'This diagram changed after your local work was saved. '
            + 'Reload it or save your work as a copy.'
          ),
        });
        setCanvasSaveStatus('conflict');
        return;
      }
      setCurrentDiagram(loaded);
      if (loaded) {
        setPersistedSignature(backendSignature);
      }
      setPersistedRevision(backendRevision);
      persistedRevisionRef.current = backendRevision;
      latestDiagramRef.current = loaded;
    }
    // If diagram not found yet (query refetch pending), do nothing — keep current canvas
  }, [contentType, objectId, editingDiagramId, diagrams]);

  const captureSaveConflict = useCallback((
    error: unknown,
    diagramId: number,
    snapshot: DiagramSaveSnapshot,
    expectedRevision: number | null,
    saveScope: string,
  ) => {
    if (!isDiagramRevisionConflict(error)) return false;
    const latestRevision = error.response.data?.latest_revision_number ?? null;
    const message = error.response.data?.detail
      ?? 'This diagram changed since you opened it. Reload it or save your work as a copy.';
    saveDiagramToStorage(
      snapshot.diagram.elements,
      snapshot.diagram.nextLegendNumber ?? 1,
      `${saveScope}:${diagramId}`,
      persistedSignature,
      expectedRevision,
    );
    if (documentScopeRef.current !== saveScope) return true;
    const conflict = {
      diagramId,
      snapshot,
      expectedRevision,
      baseSignature: persistedSignature,
      latestRevision,
      message,
    };
    setSaveConflict(conflict);
    if (!showRopeCanvasRef.current || editingIdRef.current !== diagramId) {
      canvasKeyRef.current = `${diagramId}:conflict:${expectedRevision ?? 'legacy'}`;
      loadedIdRef.current = diagramId;
      setEditingDiagramId(diagramId);
      setCurrentDiagram(snapshot.diagram);
      latestDiagramRef.current = snapshot.diagram;
      setPersistedSignature(conflict.baseSignature);
      setPersistedRevision(expectedRevision);
      persistedRevisionRef.current = expectedRevision;
      setShowRopeCanvas(true);
    }
    setCanvasSaveStatus('conflict');
    return true;
  }, [persistedSignature]);

  const doSave = useCallback(
    (
      diagram: DiagramFile,
      targetId = editingIdRef.current,
      queuedPngDataUrl?: string | null,
      expectedRevision = persistedRevisionRef.current,
    ) => {
      const currentId = targetId;
      const saveScope = `${contentType}:${objectId}`;
      const createSessionKey = canvasKeyRef.current;
      const submittedSignature = diagramSignature(
        diagram.elements,
        diagram.nextLegendNumber ?? 1,
      );
      const saveSnapshot = queuedPngDataUrl === undefined
        ? createDiagramSaveSnapshot(diagram, exportPngRef.current)
        : { diagram, pngDataUrl: queuedPngDataUrl };
      const formData = createCanvasFormData(diagram, saveSnapshot.pngDataUrl);

      if (currentId) {
        if (saveConflictRef.current?.diagramId === currentId) {
          saveDiagramToStorage(
            diagram.elements,
            diagram.nextLegendNumber ?? 1,
            `${contentType}:${objectId}:${currentId}`,
            persistedSignature,
            expectedRevision,
          );
          setCanvasSaveStatus('conflict');
          return;
        }
        formData.append('expected_revision', String(expectedRevision ?? 0));
        if (updatingDiagramIdsRef.current.has(currentId)) {
          pendingUpdateDiagramsRef.current.set(currentId, { ...saveSnapshot, expectedRevision });
          setCanvasSaveStatus('dirty');
          return;
        }
        updatingDiagramIdsRef.current.add(currentId);
        setCanvasSaveStatus('saving');
        saveCanvasMutation.mutate(
          { id: currentId, formData },
          {
            onSuccess: (saved) => {
              updatingDiagramIdsRef.current.delete(currentId);
              const savedRevision = saved.latest_revision_number ?? null;
              if (savedRevision !== null) {
                confirmedRevisionByDiagramRef.current.set(currentId, savedRevision);
              }
              reconcileDiagramStorage(
                `${contentType}:${objectId}:${currentId}`,
                diagram.elements,
                diagram.nextLegendNumber ?? 1,
                savedRevision,
              );
              const isActiveDiagram = (
                showRopeCanvasRef.current
                && editingIdRef.current === currentId
              );
              if (isActiveDiagram) {
                setPersistedSignature(submittedSignature);
                setPersistedRevision(savedRevision);
                persistedRevisionRef.current = savedRevision;
              }
              const pending = pendingUpdateDiagramsRef.current.get(currentId);
              pendingUpdateDiagramsRef.current.delete(currentId);
              const latest = pending?.diagram ?? (isActiveDiagram ? latestDiagramRef.current : null);
              const latestPngDataUrl = pending
                ? pending.pngDataUrl
                : (latest && isActiveDiagram ? exportPngRef.current?.() ?? null : null);
              const latestSignature = latest
                ? diagramSignature(latest.elements, latest.nextLegendNumber ?? 1)
                : submittedSignature;
              if (latest && latestSignature !== submittedSignature) {
                saveDiagramToStorage(
                  latest.elements,
                  latest.nextLegendNumber ?? 1,
                  `${contentType}:${objectId}:${currentId}`,
                  submittedSignature,
                  savedRevision,
                );
                if (isActiveDiagram && saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveTimeoutRef.current = null;
                }
                setTimeout(
                  () => doSaveRef.current(latest, currentId, latestPngDataUrl, savedRevision),
                  0,
                );
              }
              if (isActiveDiagram) {
                setCanvasSaveStatus(
                  latestSignature === submittedSignature ? 'saved' : 'dirty',
                );
              }
            },
            onError: (error) => {
              updatingDiagramIdsRef.current.delete(currentId);
              const pending = pendingUpdateDiagramsRef.current.get(currentId);
              pendingUpdateDiagramsRef.current.delete(currentId);
              if (captureSaveConflict(
                error,
                currentId,
                pending ?? saveSnapshot,
                pending?.expectedRevision ?? expectedRevision,
                saveScope,
              )) {
                return;
              }
              if (pending) {
                if (editingIdRef.current === currentId) setCanvasSaveStatus('dirty');
                setTimeout(
                  () => doSaveRef.current(
                    pending.diagram,
                    currentId,
                    pending.pngDataUrl,
                    pending.expectedRevision,
                  ),
                  0,
                );
              } else if (editingIdRef.current === currentId) {
                setCanvasSaveStatus('error');
              }
            },
          },
        );
      } else {
        if (isCreatingRef.current) {
          // Queue latest diagram for retry after create completes
          pendingCreateDiagramRef.current = diagram;
          return;
        }
        isCreatingRef.current = true;
        const createRequest = createRequestSequenceRef.current + 1;
        createRequestSequenceRef.current = createRequest;
        activeCreateRequestRef.current = createRequest;
        setCreateInFlight(true);
        setCanvasSaveStatus('saving');
        formData.append('content_type', contentType);
        formData.append('object_id', String(objectId));
        formData.append('title', 'Rigging Diagram');

        createCanvasMutation.mutate(formData, {
          onSuccess: (created) => {
            const savedRevision = created.latest_revision_number ?? null;
            if (savedRevision !== null) {
              confirmedRevisionByDiagramRef.current.set(created.id, savedRevision);
            }
            const temporaryDraftKey = `${saveScope}:new`;
            const permanentDraftKey = `${saveScope}:${created.id}`;
            reconcileDiagramStorage(
              temporaryDraftKey,
              diagram.elements,
              diagram.nextLegendNumber ?? 1,
              savedRevision,
              permanentDraftKey,
            );
            const isCurrentCreateRequest =
              activeCreateRequestRef.current === createRequest;
            if (isCurrentCreateRequest) {
              activeCreateRequestRef.current = null;
              isCreatingRef.current = false;
              setCreateInFlight(false);
            }
            const isActiveNewDiagram = (
              isCurrentCreateRequest
              && documentScopeRef.current === saveScope
              && canvasKeyRef.current === createSessionKey
              && showRopeCanvasRef.current
              && editingIdRef.current === null
            );
            if (!isActiveNewDiagram) {
              if (isCurrentCreateRequest) pendingCreateDiagramRef.current = null;
              return;
            }
            const latest = (
              pendingCreateDiagramRef.current
              ?? latestDiagramRef.current
            );
            const latestSignature = latest
              ? diagramSignature(latest.elements, latest.nextLegendNumber ?? 1)
              : submittedSignature;
            if (latest && latestSignature !== submittedSignature) {
              saveDiagramToStorage(
                latest.elements,
                latest.nextLegendNumber ?? 1,
                permanentDraftKey,
                submittedSignature,
                savedRevision,
              );
            }
            loadedIdRef.current = created.id;
            editingIdRef.current = created.id;
            setPersistedSignature(submittedSignature);
            setPersistedRevision(savedRevision);
            persistedRevisionRef.current = savedRevision;
            setEditingDiagramId(created.id);
            setCanvasSaveStatus(
              latestSignature === submittedSignature ? 'saved' : 'dirty',
            );
            // Flush pending save if user made changes while create was in-flight
            if (pendingCreateDiagramRef.current) {
              const pending = pendingCreateDiagramRef.current;
              pendingCreateDiagramRef.current = null;
              setTimeout(
                () => doSaveRef.current(pending, created.id, undefined, savedRevision),
                0,
              );
            }
          },
          onError: () => {
            if (activeCreateRequestRef.current !== createRequest) return;
            activeCreateRequestRef.current = null;
            isCreatingRef.current = false;
            setCreateInFlight(false);
            pendingCreateDiagramRef.current = null;
            if (
              documentScopeRef.current === saveScope
              && canvasKeyRef.current === createSessionKey
              && showRopeCanvasRef.current
            ) {
              setCanvasSaveStatus('error');
            }
          },
        });
      }
    },
    [saveCanvasMutation, createCanvasMutation, contentType, objectId, captureSaveConflict, persistedSignature],
  );

  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;

  const handleCanvasChange = useCallback(
    (next: DiagramFile) => {
      if (!canEditRef.current) return;
      latestDiagramRef.current = next;
      const targetId = editingIdRef.current;
      if (targetId && saveConflictRef.current?.diagramId === targetId) {
        saveDiagramToStorage(
          next.elements,
          next.nextLegendNumber ?? 1,
          `${contentType}:${objectId}:${targetId}`,
          persistedSignature,
          persistedRevisionRef.current,
        );
        setSaveConflict((current) => current && current.diagramId === targetId
          ? {
              ...current,
              snapshot: createDiagramSaveSnapshot(next, exportPngRef.current),
            }
          : current);
        setCanvasSaveStatus('conflict');
        return;
      }
      setCanvasSaveStatus('dirty');
      const sessionKey = canvasKeyRef.current;
      const saveSnapshot = createDiagramSaveSnapshot(next, exportPngRef.current);

      // Debounced auto-save
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        const resolvedTargetId = resolveDiagramSaveTarget(
          targetId,
          sessionKey,
          editingIdRef.current,
          canvasKeyRef.current,
          showRopeCanvasRef.current,
        );
        if (resolvedTargetId === undefined) return;
        doSaveRef.current(next, resolvedTargetId, saveSnapshot.pngDataUrl);
      }, 1500);
    },
    [contentType, objectId, persistedSignature],
  );

  const isDirtyCanvas = canvasSaveStatus === 'dirty' || canvasSaveStatus === 'conflict';

  useEffect(() => {
    onDirtyChange?.(isDirtyCanvas && showRopeCanvas);
  }, [isDirtyCanvas, showRopeCanvas, onDirtyChange]);

  const handleUpload = () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('file_name', fileName || selectedFile.name);
    formData.append('caption', caption);
    formData.append('content_type', contentType);
    formData.append('object_id', String(objectId));
    uploadMutation.mutate(formData, {
      onSuccess: () => {
        setSelectedFile(null);
        setFileName('');
        setCaption('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  };



  const handleAddSketch = () => {
    if (isCreatingRef.current) return;
    newCanvasSessionRef.current += 1;
    canvasKeyRef.current = `new:${contentType}:${objectId}:${newCanvasSessionRef.current}`;
    setEditingDiagramId(null);
    setCurrentDiagram(null);
    latestDiagramRef.current = null;
    setShowRopeCanvas(true);
    setCanvasSaveStatus('clean');
  };

  const handleSelectDiagram = (id: number) => {
    const conflict = saveConflictRef.current;
    if (conflict?.diagramId === id) {
      canvasKeyRef.current = `${id}:conflict:${conflict.expectedRevision ?? 'legacy'}`;
      loadedIdRef.current = id;
      setEditingDiagramId(id);
      setCurrentDiagram(conflict.snapshot.diagram);
      latestDiagramRef.current = conflict.snapshot.diagram;
      setPersistedSignature(conflict.baseSignature);
      setPersistedRevision(conflict.expectedRevision);
      persistedRevisionRef.current = conflict.expectedRevision;
      setShowRopeCanvas(true);
      setCanvasSaveStatus('conflict');
      return;
    }
    canvasKeyRef.current = id;
    setEditingDiagramId(id);
    setShowRopeCanvas(true);
    setCanvasSaveStatus('clean');
  };

  const handleRenameSubmit = (id: number) => {
    if (!renameValue.trim()) {
      setRenameConflict(null);
      setRenamingId(null);
      return;
    }
    const formData = new FormData();
    formData.append('title', renameValue.trim());
    const listedRevision =
      diagrams.find((diagram) => diagram.id === id)?.latest_revision_number ?? 0;
    const latestRevision = Math.max(
      listedRevision,
      confirmedRevisionByDiagramRef.current.get(id) ?? 0,
    );
    formData.append('expected_revision', String(latestRevision));
    renameCanvasMutation.mutate(
      { id, formData },
      {
        onSuccess: (saved) => {
          if (saved.latest_revision_number != null) {
            confirmedRevisionByDiagramRef.current.set(
              id,
              saved.latest_revision_number,
            );
          }
          setRenameConflict(null);
          setRenamingId(null);
        },
        onError: (error) => {
          if (isDiagramRevisionConflict(error)) {
            const currentRevision =
              error.response.data?.latest_revision_number ?? null;
            if (currentRevision !== null) {
              confirmedRevisionByDiagramRef.current.set(id, currentRevision);
            }
            setRenameConflict({
              diagramId: id,
              message: (
                error.response.data?.detail
                ?? 'This diagram changed since the list loaded. Refresh and try the rename again.'
              ),
            });
            void refetchDiagrams();
          }
        },
      },
    );
  };

  const handleReloadConflict = () => {
    if (!saveConflict) return;
    const conflictedDiagramId = saveConflict.diagramId;
    const conflictScope = documentScopeRef.current;
    const conflictSessionKey = canvasKeyRef.current;
    const isActiveConflictSession = () => (
      documentScopeRef.current === conflictScope
      && canvasKeyRef.current === conflictSessionKey
      && editingIdRef.current === conflictedDiagramId
    );
    pendingUpdateDiagramsRef.current.delete(saveConflict.diagramId);
    updatingDiagramIdsRef.current.delete(saveConflict.diagramId);
    setCanvasSaveStatus('saving');
    void refetchDiagrams().then((result) => {
      const fresh = result.data?.results?.find((diagram) => diagram.id === conflictedDiagramId);
      if (!fresh?.canvas_data) {
        if (isActiveConflictSession()) setCanvasSaveStatus('conflict');
        return;
      }
      const loaded = fromBackendSchema(fresh.canvas_data);
      if (!loaded) {
        if (isActiveConflictSession()) setCanvasSaveStatus('conflict');
        return;
      }
      clearDiagramStorage(`${conflictScope}:${conflictedDiagramId}`);
      if (!isActiveConflictSession()) return;
      canvasKeyRef.current = `${conflictedDiagramId}:reload:${fresh.latest_revision_number ?? 'latest'}`;
      loadedIdRef.current = conflictedDiagramId;
      setCurrentDiagram(loaded);
      setPersistedSignature(diagramSignature(loaded.elements, loaded.nextLegendNumber ?? 1));
      setPersistedRevision(fresh.latest_revision_number ?? null);
      persistedRevisionRef.current = fresh.latest_revision_number ?? null;
      if (fresh.latest_revision_number != null) {
        confirmedRevisionByDiagramRef.current.set(
          conflictedDiagramId,
          fresh.latest_revision_number,
        );
      }
      latestDiagramRef.current = loaded;
      setSaveConflict(null);
      setCanvasSaveStatus('clean');
    }).catch(() => {
      if (isActiveConflictSession()) setCanvasSaveStatus('conflict');
    });
  };

  const handleSaveConflictAsCopy = () => {
    if (!saveConflict) return;
    const { snapshot, diagramId } = saveConflict;
    const copiedDiagram = cloneDiagramWithFreshElementIds(snapshot.diagram);
    const conflictScope = documentScopeRef.current;
    const conflictSessionKey = canvasKeyRef.current;
    const isActiveConflictSession = () => (
      documentScopeRef.current === conflictScope
      && canvasKeyRef.current === conflictSessionKey
      && editingIdRef.current === diagramId
    );
    const formData = createCanvasFormData(copiedDiagram, snapshot.pngDataUrl);
    formData.append('content_type', contentType);
    formData.append('object_id', String(objectId));
    const originalTitle = diagrams.find((diagram) => diagram.id === diagramId)?.title || 'Rigging Diagram';
    formData.append('title', `${originalTitle} (copy)`);

    setCanvasSaveStatus('saving');
    createCanvasMutation.mutate(formData, {
      onSuccess: (created) => {
        const savedRevision = created.latest_revision_number ?? null;
        const submittedSignature = diagramSignature(
          copiedDiagram.elements,
          copiedDiagram.nextLegendNumber ?? 1,
        );
        clearDiagramStorage(`${conflictScope}:${diagramId}`);
        if (!isActiveConflictSession()) return;
        loadedIdRef.current = created.id;
        editingIdRef.current = created.id;
        canvasKeyRef.current = created.id;
        setSaveConflict(null);
        setPersistedSignature(submittedSignature);
        setPersistedRevision(savedRevision);
        persistedRevisionRef.current = savedRevision;
        if (savedRevision !== null) {
          confirmedRevisionByDiagramRef.current.set(created.id, savedRevision);
        }
        setEditingDiagramId(created.id);
        setCurrentDiagram(copiedDiagram);
        latestDiagramRef.current = copiedDiagram;
        setCanvasSaveStatus('saved');
      },
      onError: () => {
        if (isActiveConflictSession()) setCanvasSaveStatus('conflict');
      },
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Diagrams &amp; Sketches
            {showRopeCanvas && isDirtyCanvas && (
              <span className="flex items-center gap-1 text-amber-600 text-xs font-normal">
                <AlertTriangle className="h-3.5 w-3.5" />
                Unsaved diagram
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && <p className="text-sm text-gray-500">Loading diagrams...</p>}

          {imageDiagrams.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {imageDiagrams.map((diagram) => (
                <div
                  key={diagram.id}
                  className="group relative border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setLightboxUrl(diagram.file)}
                >
                  <img
                    src={diagram.file}
                    alt={diagram.file_name}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{diagram.file_name}</p>
                    {diagram.caption && (
                      <p className="text-xs text-gray-500 truncate">{diagram.caption}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Canvas diagrams list (multi-diagram support) */}
          {canvasDiagrams.length > 0 && !showRopeCanvas && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Rigging Sketches</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {canvasDiagrams.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-2 border rounded-lg p-2 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => {
                      if (!canEdit && d.file) {
                        setLightboxUrl(d.file);
                      } else {
                        // Locked plans without a preview open the canvas read-only:
                        // handleCanvasChange ignores edits when canEdit is false.
                        handleSelectDiagram(d.id);
                      }
                    }}
                  >
                    {d.file && (
                      <img
                        src={d.file}
                        alt={d.title}
                        className="h-12 w-16 rounded object-cover border"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {renamingId === d.id ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            className="h-6 text-xs"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameSubmit(d.id);
                              if (e.key === 'Escape') {
                                setRenameConflict(null);
                                setRenamingId(null);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            aria-label="Save rename"
                            onClick={() => handleRenameSubmit(d.id)}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm font-medium truncate">{d.title || 'Untitled Sketch'}</p>
                      )}
                      {renameConflict?.diagramId === d.id && (
                        <p className="text-xs text-red-600" role="alert">
                          {renameConflict.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {formatDateTimeInTimeZone(d.uploaded_at, timeZone, { dateStyle: 'medium' })}
                      </p>
                    </div>
                    {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 shrink-0"
                      aria-label={`Rename ${d.title || 'Untitled Sketch'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameConflict(null);
                        setRenamingId(d.id);
                        setRenameValue(d.title || '');
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {diagrams.length === 0 && !isLoading && (
            <p className="text-sm text-gray-500">No diagrams uploaded yet.</p>
          )}

          {/* Rigging canvas editor */}
          {showRopeCanvas && (
            <div className="border-t pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">
                    {editingDiagramId
                      ? diagrams.find((d) => d.id === editingDiagramId)?.title || 'Rigging Diagram'
                      : 'New Rigging Diagram'}
                  </p>
                  {canvasSaveStatus === 'saving' && (
                    <span className="text-xs text-gray-400">Saving…</span>
                  )}
                  {canvasSaveStatus === 'saved' && (
                    <span className="text-xs text-green-600">Saved</span>
                  )}
                  {canvasSaveStatus === 'error' && (
                    <span className="text-xs text-red-600">Save failed</span>
                  )}
                  {canvasSaveStatus === 'conflict' && (
                    <span className="text-xs text-red-600">Save conflict</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={Boolean(saveConflict)}
                  onClick={() => {
                    // Flush pending debounced save before closing
                    if (saveTimeoutRef.current) {
                      clearTimeout(saveTimeoutRef.current);
                      saveTimeoutRef.current = null;
                      if (latestDiagramRef.current) {
                        doSaveRef.current(latestDiagramRef.current);
                      }
                    }
                    setShowRopeCanvas(false);
                    setEditingDiagramId(null);
                    setCanvasSaveStatus('clean');
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Close
                </Button>
              </div>
              {saveConflict && saveConflict.diagramId === editingDiagramId && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
                  <p className="font-medium">This diagram has newer saved changes.</p>
                  <p>{saveConflict.message}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={canvasSaveStatus === 'saving'}
                      onClick={handleReloadConflict}
                    >
                      Reload diagram
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={canvasSaveStatus === 'saving'}
                      onClick={handleSaveConflictAsCopy}
                    >
                      Save as copy
                    </Button>
                  </div>
                </div>
              )}
              <div className="h-[600px] rounded-lg border overflow-hidden">
                {!canEdit && (
                  <p className="text-sm text-muted-foreground">
                    Read-only: this plan is no longer a draft, so changes to this sketch are not saved.
                  </p>
                )}
                <RiggingCanvas
                  key={canvasKeyRef.current}
                  value={currentDiagram}
                  onChange={handleCanvasChange}
                  exportPngRef={exportPngRef}
                  draftKey={
                    editingDiagramId
                      ? `${contentType}:${objectId}:${editingDiagramId}`
                      : `${contentType}:${objectId}:new`
                  }
                  persistedSignature={persistedSignature}
                  persistedRevision={persistedRevision}
                  loading={Boolean(editingDiagramId && loadedIdRef.current !== editingDiagramId)}
                />
              </div>
            </div>
          )}

          {!canEdit && (
            <p className="text-sm text-muted-foreground border-t pt-4">
              Diagrams are locked once the plan leaves draft. Create a new revision to change them.
            </p>
          )}
          {canEdit && (
          <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Add Diagram</p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddSketch}
                    disabled={showRopeCanvas || createInFlight || Boolean(saveConflict)}
                  >
                    <PenTool className="h-4 w-4 mr-1" />
                    {canvasDiagrams.length > 0 ? 'Add Sketch' : 'Rope Diagram'}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="diagramTitle" className="text-sm">Title</Label>
                  <Input
                    id="diagramTitle"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Diagram title"
                  />
                </div>
                <div>
                  <Label htmlFor="diagramDesc" className="text-sm">Caption</Label>
                  <Input
                    id="diagramDesc"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Optional caption"
                  />
                </div>
              </div>
              {/* The upload block is its own required-field validation
                  boundary: the page-level "Submit for Review" button must not
                  be blocked by this (always rendered) required file input. */}
              <form
                className="flex items-center gap-3"
                data-required-validation-boundary="true"
                onSubmit={(e) => e.preventDefault()}
              >
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,application/pdf"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                  className="flex-1"
                  required
                />
                <Button
                  type="button"
                  size="sm"
                  data-validate-required="true"
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </Button>
              </form>
              {uploadMutation.isError && (
                <p role="alert" className="text-sm text-red-600">
                  {isAxiosError<{ detail?: string }>(uploadMutation.error) && uploadMutation.error.response?.data?.detail
                    ? uploadMutation.error.response.data.detail
                    : 'Upload failed. Diagrams can only be added to draft plans.'}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={lightboxUrl}
            alt="Diagram"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
